import { AutoAwesome, Add, MoveDown } from '@mui/icons-material';
import ChecklistIcon from '@mui/icons-material/Checklist';
import { Stack, Typography as MuiTypography, CircularProgress } from '@mui/material';
import { Typography } from 'components/index';
import React from 'react';
import { ReportLineItem, ReportStatusEnum } from './report.type';
import { TOOLTIPS } from './report.util';
import { Division } from '../glossary/glossary.type';
import { IconOverTextButton } from '../common/IconOverTextButton';
import { IconOverTextDropdown, DropdownOption } from '../common/IconOverTextDropdown';

interface ReportDivisionCustomToolbarProps {
  headingText: React.ReactNode;
  division: Division | null;
  predictingDivisions: boolean;
  isLocked: boolean | undefined;
  reportStatus: string;
  handleOpenProjectDialog: (divisionId: string, divisionCode: string, divisionName: string) => void;
  handleOpenLineItemDialog: () => void;
  handleAutoAssignDivisions: () => void;

  selectedLineItems?: ReportLineItem[];
  divisions?: Division[];
  onMoveLineItemsToDivision?: (targetDivisionId: string) => void;
}

const ReportDivisionCustomToolbar: React.FC<ReportDivisionCustomToolbarProps> = ({
  headingText,
  division,
  predictingDivisions,
  isLocked,
  reportStatus,
  handleOpenProjectDialog,
  handleOpenLineItemDialog,
  handleAutoAssignDivisions,
  selectedLineItems = [],
  divisions = [],
  onMoveLineItemsToDivision,
}) => {
  const [loadingDivisionId, setLoadingDivisionId] = React.useState<string | null>(null);

  const handleDivisionSelect = async (targetDivisionId: string) => {
    setLoadingDivisionId(targetDivisionId);
    if (onMoveLineItemsToDivision) {
      await onMoveLineItemsToDivision(targetDivisionId);
    }
    setLoadingDivisionId(null);
  };

  // Create dropdown options from divisions
  const divisionOptions: DropdownOption<string>[] = divisions
    .filter(div => div.id !== division?.id)
    .map(div => ({
      value: div.id,
      label: (
        <Stack direction='row' alignItems='center' spacing={1}>
          <MuiTypography variant='body2'>
            {div.code} - {div.name}
          </MuiTypography>
          {loadingDivisionId === div.id && <CircularProgress size={16} thickness={5} />}
        </Stack>
      ),
      loading: loadingDivisionId === div.id,
      id: div.id,
    }));

  const isDropdownDisabled =
    isLocked ||
    selectedLineItems.length === 0 ||
    divisions.length <= 1 ||
    (division ? divisions.filter(div => div.id !== division.id).length === 0 : false);

  const ActionsMenu = () => (
    <Stack margin={'auto'} direction={'row'} spacing={0} alignItems='center'>
      {/* Move Divisions Button */}
      <IconOverTextDropdown
        icon={<MoveDown />}
        text={`Move Divisions (${selectedLineItems.length})`}
        tooltip={
          isLocked && reportStatus === ReportStatusEnum.Completed
            ? TOOLTIPS.statusCompleted
            : isLocked
              ? TOOLTIPS.unlockToEdit
              : selectedLineItems.length === 0
                ? 'Select line items to move them to another division'
                : 'Move selected line items to another division'
        }
        options={divisionOptions}
        disabled={isDropdownDisabled}
        onSelect={handleDivisionSelect}
        showHoverEffect={true}
        wrapInSpan={true}
      />

      {/* Buttons shown when division exists */}
      {division && (
        <>
          <IconOverTextButton
            icon={<Add />}
            text='Add Line Item'
            tooltip={
              isLocked && reportStatus === ReportStatusEnum.Completed
                ? TOOLTIPS.statusCompleted
                : isLocked
                  ? TOOLTIPS.unlockToEdit
                  : 'Add a new line item to this division'
            }
            onClick={handleOpenLineItemDialog}
            disabled={isLocked}
            showHoverEffect={true}
            wrapInSpan={true}
          />

          <IconOverTextButton
            icon={<ChecklistIcon />}
            text='Manage Budgets'
            tooltip={
              isLocked && reportStatus === ReportStatusEnum.Completed
                ? TOOLTIPS.statusCompleted
                : isLocked
                  ? TOOLTIPS.unlockToEdit
                  : 'Manage budgets for this division'
            }
            onClick={() => {
              handleOpenProjectDialog(division.id, division.code, division.name);
            }}
            disabled={isLocked}
            showHoverEffect={true}
            wrapInSpan={true}
          />
        </>
      )}

      {/* Auto Assign Button when division is null */}
      {!division && (
        <IconOverTextButton
          icon={<AutoAwesome />}
          text='Auto Assign'
          tooltip={
            isLocked && reportStatus === ReportStatusEnum.Completed
              ? TOOLTIPS.statusCompleted
              : isLocked
                ? TOOLTIPS.unlockToEdit
                : 'Click to predict divisions for line items using Artificial Intelligence'
          }
          onClick={handleAutoAssignDivisions}
          loading={predictingDivisions}
          disabled={isLocked}
          showHoverEffect={true}
          wrapInSpan={true}
        />
      )}
    </Stack>
  );

  return (
    <Stack alignItems={'center'} justifyContent={'space-between'} direction={'row'} p={2}>
      <Stack direction={'row'} spacing={2}>
        <Typography color='black' variant='h6'>
          {headingText}
        </Typography>
      </Stack>
      <Stack direction={'row'} spacing={2}>
        <ActionsMenu />
      </Stack>
    </Stack>
  );
};

export default ReportDivisionCustomToolbar;
